import { useQueryClient } from "@tanstack/react-query";

import {
  getGetCategoriesQueryKey,
  useUpdateCategory,
} from "@vega-api/generated/case-admin-v1/case-admin-v1";
import { type CategoryResponse } from "@vega-api/generated/types/categoryResponse";
import { CategoryUpdateRequestStatus } from "@vega-api/generated/types/categoryUpdateRequestStatus";
import { Switch } from "@vega-core/components/Switch/Switch";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";

import { useMutationErrorToast } from "../../hooks/useMutationErrorToast";

type CategoryActiveToggleProps = {
  category: CategoryResponse;
};

export const CategoryActiveToggle = ({
  category,
}: CategoryActiveToggleProps) => {
  const showToast = useToast();
  const errorToast = useMutationErrorToast();
  const queryClient = useQueryClient();

  const isActive = category.status !== CategoryUpdateRequestStatus.INACTIVE;

  const { mutate: updateCategory, isPending } = useUpdateCategory({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetCategoriesQueryKey() });
        showToast({
          title: m.cases_settings_success_updateCategory(),
          variant: "success",
        });
      },
      onError: errorToast(m.cases_settings_error_updateCategory()),
    },
  });

  const handleChange = (checked: boolean) => {
    updateCategory({
      categoryId: category.id,
      data: {
        status: checked
          ? CategoryUpdateRequestStatus.ACTIVE
          : CategoryUpdateRequestStatus.INACTIVE,
      },
    });
  };

  return (
    <Switch
      ariaLabel={m.cases_settings_category_activeToggleAria({
        name: category.name,
      })}
      checked={isActive}
      dataTestId={`category-active-toggle-${category.id}`}
      disabled={category.isDefault}
      isLoading={isPending}
      onChange={handleChange}
    />
  );
};
